/**
 * Hover / focus / highlight states and cmdk attribute selectors can't be
 * expressed as inline styles, so the shell injects one scoped <style>
 * block. Tokens only — both themes resolve automatically.
 *
 * (Workstream A owns the design-system CSS files; this stays shell-local.)
 */

const STYLE_ID = 'it-shell-style'

const SHELL_CSS = `
.it-skip-link {
  position: fixed; top: 8px; left: 8px; z-index: 100;
  padding: 8px 12px; border-radius: var(--r-sm);
  background: var(--bg-elev); color: var(--text);
  border: 1px solid var(--border); box-shadow: var(--sh-3);
  font-size: 13px; text-decoration: none;
  transform: translateY(-200%);
  transition: transform var(--dur-fast) var(--ease-out);
}
.it-skip-link:focus { transform: translateY(0); }

.it-nav-link { text-decoration: none; }
.it-nav-link:hover { background: var(--bg-sunk); }
.it-icon-btn:hover { background: var(--bg-sunk); color: var(--text); }
.it-menu-item[data-highlighted] { background: var(--bg-sunk); color: var(--text); }

.it-nav-link:focus-visible,
.it-icon-btn:focus-visible,
.it-menu-item:focus-visible,
.it-shell-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

@keyframes it-cmdk-overlay-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes it-cmdk-dialog-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-8px) scale(0.98); }
  to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
}
[cmdk-overlay] {
  position: fixed; inset: 0; z-index: 80;
  background: color-mix(in srgb, var(--text) 32%, transparent);
  animation: it-cmdk-overlay-in var(--dur-base) var(--ease-out);
}
[cmdk-dialog] {
  position: fixed; z-index: 81; top: 14vh; left: 50%;
  transform: translateX(-50%);
  width: min(620px, calc(100vw - 32px));
  animation: it-cmdk-dialog-in var(--dur-base) var(--ease-out);
}
[cmdk-root] {
  background: var(--bg-elev); border: 1px solid var(--border);
  border-radius: var(--r-md); box-shadow: var(--sh-5);
  overflow: hidden; font-family: var(--ff-ui); color: var(--text);
}
[cmdk-input] {
  width: 100%; border: 0; outline: none; background: transparent;
  padding: 14px 16px; font-size: 14px; color: var(--text);
  border-bottom: 1px solid var(--border); font-family: var(--ff-ui);
}
[cmdk-input]::placeholder { color: var(--text-faint); }
[cmdk-list] { max-height: 360px; overflow: auto; padding: 6px; scroll-padding-block: 6px; }
[cmdk-group-heading] {
  padding: 8px 10px 4px; font-size: 11px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint);
}
[cmdk-item] {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: var(--r-sm);
  font-size: 13px; color: var(--text-soft); cursor: pointer;
}
[cmdk-item][data-selected='true'] { background: var(--bg-sunk); color: var(--text); }
[cmdk-empty] { padding: 16px; font-size: 13px; color: var(--text-mute); text-align: center; }

@media (prefers-reduced-motion: reduce) {
  .it-skip-link { transition: none; }
}
`

export function ensureShellStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID)) return
  const el = document.createElement('style')
  el.id = STYLE_ID
  el.textContent = SHELL_CSS
  document.head.appendChild(el)
}
